import { TZDate } from '@date-fns/tz';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { format } from 'date-fns';
import { ZURICH_TZ } from '@shared/constants';
import { GENERAL_ERROR_MESSAGE } from '@shared/models/messages';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { InsightsAuthService } from '@core/services/insights-auth.service';
import {
  InsightsActionOutcome,
  InsightsActionPayload,
  InsightsActionResponse,
  InsightsQueryOutcome,
  InsightsQueryResponse,
  PersistedGenerationOutcome,
  PersistedInsightGeneration,
} from './models/insights.model';

@Injectable()
export class InsightsApiService {
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(SnackBarService);
  private readonly insightsAuth = inject(InsightsAuthService);
  private readonly authService = inject(AuthService);

  private readonly actionUrl = `${environment.insightsApiUrl}${this.withOntology(environment.insightsActionPath)}`;
  private readonly queryUrl = `${environment.insightsApiUrl}${this.withOntology(environment.insightsQueryPath)}`;
  private readonly persistedUrl = `${environment.apiUrl}/v2/assessments`;

  getPersistedGeneration$(assessmentId: string): Observable<PersistedGenerationOutcome> {
    return this.http.get<PersistedInsightGeneration>(`${this.persistedUrl}/${assessmentId}/insight`).pipe(
      map(generation => ({ kind: 'found', generation }) as PersistedGenerationOutcome),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          const message = String(error.error?.message ?? error.error ?? '');
          const normalizedMessage = message.toLowerCase();
          let kind: PersistedGenerationOutcome['kind'] = 'error';
          if (normalizedMessage.includes('assessment not found')) {
            kind = 'assessmentMissing';
          } else if (normalizedMessage.includes('insights not found')) {
            kind = 'missingGeneration';
          }
          return of<PersistedGenerationOutcome>({
            kind,
          });
        }
        return of<PersistedGenerationOutcome>({ kind: 'error' });
      })
    );
  }

  saveGeneration$(
    generation: Omit<PersistedInsightGeneration, 'assessmentId' | 'createdAt' | 'updatedAt'> & { assessmentId: string | number }
  ): Observable<boolean> {
    return this.http
      .put<PersistedInsightGeneration>(`${this.persistedUrl}/${generation.assessmentId}/insight`, {
        state: generation.state,
        stargateRecordId: generation.stargateRecordId,
        summary: generation.summary,
        stargateTimestamp: this.toSbrasLocalDateTime(generation.stargateTimestamp),
        createdBy: generation.stargateUserId,
      })
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  /** Triggers the action that generates ESG insights for an assessment. Response body is ignored. */
  triggerAction$(payload: InsightsActionPayload): Observable<InsightsActionOutcome> {
    return from(this.insightsAuth.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          this.demoteAccess();
          return of<InsightsActionOutcome>('unauthorized');
        }

        return this.http.post<InsightsActionResponse>(this.actionUrl, { parameters: payload }, { headers: this.headers(token) }).pipe(
          switchMap(response => {
            if (response?.validation?.result === 'INVALID') {
              this.snackBar.showError('Insights cannot be generated: the assessment data is invalid.');
              return of<InsightsActionOutcome>('invalid');
            }
            return of<InsightsActionOutcome>('success');
          }),
          catchError((error: HttpErrorResponse) => {
            if (this.isUnauthorized(error)) {
              this.demoteAccess();
              return of<InsightsActionOutcome>('unauthorized');
            }
            this.snackBar.showError(`Failed to generate insights: ${error?.error?.message ?? GENERAL_ERROR_MESSAGE}.`);
            return of<InsightsActionOutcome>('error');
          })
        );
      })
    );
  }

  /** Retrieves the ESG recommendation records for an assessment. */
  getRecommendation$(assessmentId: string): Observable<InsightsQueryOutcome> {
    return from(this.insightsAuth.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          this.demoteAccess();
          return of<InsightsQueryOutcome>({ kind: 'unauthorized' });
        }

        return this.http
          .post<InsightsQueryResponse>(this.queryUrl, { parameters: { assessmentId } }, { headers: this.headers(token) })
          .pipe(
            switchMap(response => of<InsightsQueryOutcome>({ kind: 'records', records: response?.value ?? [] })),
            catchError((error: HttpErrorResponse) => {
              if (this.isUnauthorized(error)) {
                this.demoteAccess();
                return of<InsightsQueryOutcome>({ kind: 'unauthorized' });
              }
              return of<InsightsQueryOutcome>({ kind: 'serviceDown' });
            })
          );
      })
    );
  }

  private headers(token: string): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });
  }

  private withOntology(path: string): string {
    return path.replace('{ontology}', environment.insightsOntology);
  }

  private toSbrasLocalDateTime(timestamp: string | null): string | null {
    if (!timestamp) {
      return timestamp;
    }

    const localDateTime = timestamp.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
    if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(localDateTime)) {
      return localDateTime;
    }

    const swissTimestamp = TZDate.tz(ZURICH_TZ, localDateTime);
    return Number.isNaN(swissTimestamp.getTime()) ? localDateTime : format(swissTimestamp, "yyyy-MM-dd'T'HH:mm:ss.SSS");
  }

  private isUnauthorized(error: HttpErrorResponse): boolean {
    return error?.status === 401 || error?.status === 403;
  }

  private demoteAccess(): void {
    this.authService.hasInsightsAccess.set(false);
  }
}
